import { writable } from 'svelte/store';

/**
 * Data Load State
 * Tracks the overall data loading state from initialization through completion
 */
export type DataLoadState = 'init' | 'loading' | 'complete';

export const dataLoadState = writable<DataLoadState>('init');

/**
 * Cloud Sync State Interface
 *
 * Tracks the state of cloud synchronization operations across all adapters.
 * This store is shared across the entire application and persists even when
 * navigating between different views.
 */
export interface CloudSyncState {
  isRunning: boolean; // Whether a sync operation is currently active
  isStopping: boolean; // Whether the user has requested to stop the sync
  progress: number; // Progress percentage (0-100)
  totalItems: number; // Total number of items to process across all adapters
  itemsComplete: number; // Number of items completed so far
  currentOperation: string; // Human-readable description of current operation
  error: string | null; // Error message if sync failed
}

const initialState: CloudSyncState = {
  isRunning: false,
  isStopping: false,
  progress: 0,
  totalItems: 0,
  itemsComplete: 0,
  currentOperation: '',
  error: null
};

export const cloudSyncStore = writable<CloudSyncState>(initialState);

/**
 * Calculate progress percentage based on completed items and total items
 *
 * PROGRESS FORMULA: (itemsComplete + 10) / (totalItems + 10) * 100
 *
 * The +10 accounts for the initial analysis phase being a significant portion
 * of the sync operation. This ensures that:
 * - Progress starts at ~10% when analysis begins (0 + 10) / (0 + 10) = 100%
 * - Progress is ~50% when half the items are done (50 + 10) / (100 + 10) = 54.5%
 * - Progress reaches 100% when all items are complete
 *
 * This formula provides better user experience by showing meaningful progress
 * even during the initial analysis phase when totalItems might be 0.
 *
 * @param itemsComplete - Number of items processed so far
 * @param totalItems - Total number of items to process
 * @returns Progress percentage (0-100)
 */
function calculateProgress(itemsComplete: number, totalItems: number): number {
  if (totalItems === 0) return 0;
  // Formula: (itemsComplete + 10) / (totalItems + 10) * 100
  return Math.min(100, Math.round(((itemsComplete + 10) / (totalItems + 10)) * 100));
}

/**
 * Cloud Sync Actions
 *
 * These actions provide a clean API for managing cloud sync operations.
 * All actions update the shared cloudSyncStore, which is observed by
 * components throughout the application.
 */
export const cloudSyncActions = {
  startSync: async () => {
    cloudSyncStore.update((state) => ({
      ...state,
      isRunning: true,
      isStopping: false,
      progress: 0,
      totalItems: 0,
      itemsComplete: 0,
      currentOperation: 'Initializing sync...',
      error: null
    }));
  },

  stopSync: () => {
    cloudSyncStore.update((state) => ({
      ...state,
      isStopping: true
    }));
  },

  updateProgress: (itemsComplete: number, totalItems: number, operation: string) => {
    cloudSyncStore.update((state) => ({
      ...state,
      itemsComplete,
      totalItems,
      progress: calculateProgress(itemsComplete, totalItems),
      currentOperation: operation
    }));
  },

  setError: (error: string) => {
    cloudSyncStore.update((state) => ({
      ...state,
      error,
      isRunning: false,
      isStopping: false
    }));
  },

  completeSync: () => {
    cloudSyncStore.update((state) => ({
      ...state,
      isRunning: false,
      isStopping: false,
      progress: 100,
      currentOperation: 'Sync completed'
    }));
  },

  reset: () => {
    cloudSyncStore.set(initialState);
  }
};
